import { Table } from 'antd';
import CertificationPackagesTableAction from 'pages/Dashboard/Components/ProductCertification/CertificationPackagesTableAction';
import { useAuthContextUser } from 'context/AuthContext';
import { ACSFilters, DSSFilters, PackageStateFilter, ProtocolVersionFilters, VstsAccessFilters } from './AdminCertificationPackagesListHelper';
import CertificationPackageState from '../../SharedComponents/CertificationPackageState';
import React, { useContext, useState } from 'react';
import { trackPromise } from 'react-promise-tracker';
import { FileHelper } from '_helpers/FileHelper';
import { GlobalAlertContext } from 'context/GlobalAlertContext';
import { ceritificationPackageService } from 'services/certification.certificationPackage';
import '../../CertificationPackageList/PackageList.css'
import AdminPackagesListFootnote from './AdminPackagesListFootnote';
import { DateHelper } from '_helpers/DateHelper';

const AdminCertificationPackagesList = ({ data, handleChangeFilter, pagination, filters, search, sorter}) => {

    const user = useAuthContextUser();
    const alertContext = useContext(GlobalAlertContext)

    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    // Function to handle expand/collapse events
    const handleExpand = (expanded, record) => {
        const keys = expanded ? [...expandedRowKeys, record.key] : expandedRowKeys.filter(key => key !== record.key);
        setExpandedRowKeys(keys);
    };

    // Function to determine row class
    const rowClass = (record, index) => {
        return expandedRowKeys.includes(record.key) ? 'outer-table-row expanded' : 'outer-table-row';
    };


    const downloadComplianceLetter = (compApprovalId, bid, packageId) => {
        trackPromise(
            ceritificationPackageService.DownloadComplianceLetter(bid, compApprovalId, packageId)
                .then((result) => {
                    FileHelper.DownloadFile(result);
                })
                .catch(error => alertContext.setErrorMessage("Unable to download Compliance Letter. Please try again later."))
        );
    }

    const INNER_COLUMNS = [
        {
            title: 'Package ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'Package Name',
            dataIndex: 'packageName',
            key: 'packageName',
        },
        {
            title: 'Region',
            dataIndex: 'region',
            key: 'region',
        },
        {
            title: 'Country',
            dataIndex: 'licensedCountry',
            key: 'licensedCountry',
        },
        {
            title: 'EMVCo Reference Number',
            dataIndex: 'emvCoRefNum',
            key: 'emvCoRefNum',
            width: 230,
        },
        {
            title: 'EMVCo Reference Number Expiry Date',
            dataIndex: 'emvCoExpiryDate',
            key: 'emvCoExpiryDate',
        },
        {
            title: 'VSTS Subscription Start/End Date',
            dataIndex: 'subscriptionDates',
            key: 'upgradeNum',
        },
        {
            title: 'Compliance Letter Expiry Date',
            dataIndex: 'compExpiryDate',
            key: 'compExpiryDate',
        },
    ];

    const OUTER_COLUMNS = [
        {
            title: 'Business Name',
            dataIndex: ['packageOuterTable', 'licensedCompanyName'],
            sorter: (a, b) => a.packageOuterTable.licensedCompanyName.localeCompare(b.packageOuterTable.licensedCompanyName),
            sortDirections: ['descend', 'ascend'],
            key: 'licensedCompanyName',
        },
        {
            title: 'BID',
            dataIndex: ['packageOuterTable', 'bid'],
            sorter: (a, b) => a.packageOuterTable.bid - b.packageOuterTable.bid,
            sortDirections: ['descend', 'ascend'],
            key: 'bid',
            width: 120
        },
        {
            title: 'Implementation Type',
            dataIndex: ['packageOuterTable', 'implementationType'],
            key: 'implementationType',
            filters: ACSFilters.concat(DSSFilters),
            filteredValue: filters.implementationType,
            onFilter: (value, record) => record.packageOuterTable.implementationType === value,
            sorter: true,
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Protocol Version',
            dataIndex: ['packageOuterTable', 'protocolVersion'],
            key: 'protocolVersion',
            filters: ProtocolVersionFilters,
            filteredValue: filters.protocolVersion,
            onFilter: (value, record) => record.packageOuterTable.protocolVersion.trim() === value,
            sorter: (a, b) => a.packageOuterTable.protocolVersion.localeCompare(b.packageOuterTable.protocolVersion),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Compliance Letter',
            dataIndex: ['packageOuterTable', 'compApprovalId',],
            key: 'compApprovalId',
            render: (text, record) => <a onClick={() => downloadComplianceLetter(text, record.packageOuterTable.bid, record.packageInnerTable.id)}>{record.packageOuterTable.compApprovalId}</a>,
        },
        {
            title: 'Package Status',
            dataIndex: ['packageOuterTable', 'packageStateDisplayName',],
            key: 'packageState',
            filters: PackageStateFilter,
            defaultFilteredValue: [1, 2, 3, 4, 5, 6, 7],
            filteredValue: filters.packageState,
            filterResetToDefaultFilteredValue: true,
            width: 150,
            onFilter: (value, record) => record.packageOuterTable.packageState === value,
            render: (text, record) => <CertificationPackageState packageStateDisplayName={text} packageState={record.packageOuterTable.packageState} />,
        },
        {
            title: 'VSTS Access',
            dataIndex: ['packageOuterTable', 'fullVstsAccessDescription'],
            key: 'vstsAccess',
            filters: VstsAccessFilters,
            filteredValue: filters.vstsAccess,
            onFilter: (value, record) => record.vstsAccess.vstsAccessDescription == value,
            width: 200,
        },
        {
            title: 'Updated On',
            dateIndex: ['packageOuterTable', 'lastUpdatedOn'],
            key: 'lastUpdatedOn',
            sorter: true,
            defaultSortOrder: 'descend',
            render: (text, record) => DateHelper.ToFormattedDate(record.packageOuterTable.lastUpdatedOn),
            width: 150
        },
        {
            title: '',
            dataIndex: ['packageOuterTable', 'packageState'],
            key: '',
            render: (text, record) => <CertificationPackagesTableAction packageId={record.packageInnerTable.id} user={user} packageState={text} 
                currentSubscription={record.dialogSubscriptionInfo} companyName={record.packageOuterTable.licensedCompanyName} 
                fetchData={handleChangeFilter} filters={filters} search={search} pagination={pagination} 
                vstsAccessObj={record.vstsAccess} sorter={sorter}/>,
        },
    ];


    return (
        <>
            <div style={{ width: '100%', overflowX: 'auto', margin: 'auto 10px' }}>
                <Table
                    rowClassName={rowClass}
                    columns={OUTER_COLUMNS}
                    expandable={{
                        expandedRowRender: (record) => <Table columns={INNER_COLUMNS} dataSource={[record.packageInnerTable]} pagination={false} />,
                        onExpand: (expanded, record) => { handleExpand(expanded, record) }
                    }}
                    dataSource={data}
                    pagination={{
                        position: ['bottomCenter'],
                        ...pagination,
                        showSizeChanger: false
                    }}
                    onChange={(pagination, filters, sorter) => handleChangeFilter(pagination, filters, search, sorter)}
                    footer={() => <AdminPackagesListFootnote />}
                />
            </div>
        </>
    );
};
export default AdminCertificationPackagesList;